import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../../../components/ui_components/FounderWrapper';
import colors from '../../../../styles/colors';
import { apiGetCards, apiDeleteCard } from '../../../../api/cardApi';
import { apiListWrestler } from '../../../../api/wrestlerApi';
import { apiListPpvByYear } from '../../../../api/ppvApi';
import { apiListCinture } from '../../../../api/beltApi';
import { apiListAllUsers } from '../../../../api/userApi';
import CardCreateModal from './CardCreateModal';
import type { Card, CardRarity } from '../../../../types/models/card';
import type { Wrestler, PPVGeneral, Belt } from '../../../../types';
import type { UserListItem } from '../../../../types/models/user';

interface RefData {
  wrestlers: Wrestler[];
  ppvs: PPVGeneral[];
  belts: Belt[];
  users: UserListItem[];
}

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 1600px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: gold;
  margin: 0;
`;

const NewBtn = styled.button`
  padding: 8px 16px;
  background-color: ${colors.bgColorBordeaux};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.bgColorRed};
  }
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const FilterSelect = styled.select`
  padding: 7px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: ${colors.bgColorBlack2};
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: gold;
  }

  option {
    background-color: ${colors.bgColorBlack2};
  }
`;

const SearchInput = styled.input`
  padding: 7px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: ${colors.bgColorBlack2};
  color: #fff;
  font-size: 14px;
  width: 200px;

  &:focus {
    outline: none;
    border-color: gold;
  }
`;

const ResultCount = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  margin-left: auto;
`;

const StatusText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin-top: 40px;
`;

const ErrorText = styled.p`
  color: #dc3545;
  text-align: center;
  margin-top: 40px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CardItem = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
`;

const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 150%; /* 1248/832 = portrait 2:3 */
  background-color: #1a1a1a;
  flex-shrink: 0;
  flex-grow: 0;
  overflow: hidden;
`;

const CardImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CardRetroOverlay = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
  background-color: #1a1a1a;

  ${CardImageWrapper}:hover & {
    opacity: 1;
  }
`;

const RetroLabel = styled.span`
  position: absolute;
  top: 6px;
  left: 6px;
  background-color: rgba(0, 0, 0, 0.65);
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
  letter-spacing: 0.5px;
  padding: 2px 5px;
  border-radius: 3px;
  z-index: 1;
`;

const CardImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.2);
  font-size: 13px;
`;

const rarityColor: Record<CardRarity, string> = {
  common: '#888',
  rare: '#3b82f6',
  epic: '#a855f7',
  legendary: 'gold',
};

const RarityBadge = styled.span<{ rarity: CardRarity }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${({ rarity }) => rarityColor[rarity]};
  color: ${({ rarity }) => (rarity === 'legendary' ? '#000' : '#fff')};
  align-self: flex-start;
`;

const CardInfo = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const WrestlerNames = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  margin: 0;
  line-height: 1.4;
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Tag = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  background-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const EditBtn = styled.button`
  margin-top: auto;
  padding: 7px 0;
  background-color: transparent;
  color: gold;
  border: 1px solid gold;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: rgba(255, 215, 0, 0.1);
  }
`;

const DeleteBtn = styled.button`
  padding: 7px 0;
  background-color: transparent;
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.5);
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: rgba(220, 53, 69, 0.1);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DeleteError = styled.p`
  color: #dc3545;
  font-size: 11px;
  margin: 0;
  line-height: 1.3;
`;

const AuthorRow = styled.p`
  margin: 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;

  span {
    color: rgba(255, 215, 0, 0.7);
    font-style: normal;
    font-weight: 600;
  }
`;

const PriceBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  color: gold;
  background-color: rgba(255,215,0,0.1);
  border: 1px solid rgba(255,215,0,0.3);
  border-radius: 4px;
  padding: 2px 7px;
`;

const NoPriceBadge = styled.span`
  font-size: 11px;
  color: rgba(255,255,255,0.2);
  font-style: italic;
`;

const DisabledPriceBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.35);
  background-color: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 4px;
  padding: 2px 7px;
  text-decoration: line-through;
`;

const RARITIES: CardRarity[] = ['common', 'rare', 'epic', 'legendary'];

const CardListPage: React.FC = () => {
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRarity, setSelectedRarity] = useState<CardRarity | ''>('');
  const [selectedExpansionId, setSelectedExpansionId] = useState<number | ''>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [refData, setRefData] = useState<RefData | null>(null);
  const [refDataLoading, setRefDataLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteErrors, setDeleteErrors] = useState<Record<number, string>>({});

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCard(null);
  };

  const handleDeleteCard = async (card: Card) => {
    if (!window.confirm(`Eliminare la carta #${card.id}? L'operazione è irreversibile.`)) return;
    setDeletingId(card.id);
    setDeleteErrors((prev) => { const n = { ...prev }; delete n[card.id]; return n; });
    try {
      await apiDeleteCard(card.id);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
    } catch (err: any) {
      const msg = err?.errors?.card ?? err?.message ?? 'Eliminazione fallita.';
      setDeleteErrors((prev) => ({ ...prev, [card.id]: msg }));
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenModal = async () => {
    setIsModalOpen(true);
    if (refData !== null || refDataLoading) return;
    setRefDataLoading(true);
    try {
      const [wrestlersRaw, ppvsByYearRaw, beltsRaw, usersRaw] = await Promise.all([
        apiListWrestler(),
        apiListPpvByYear(),
        apiListCinture(),
        apiListAllUsers(),
      ]);
      const toArr = (d: any) => Array.isArray(d) ? d : (d?.data ?? []);
      // Costruisce PPVGeneral[] con le edizioni specifiche (ppvs) già popolate
      // La risposta è { "2005": [...], "2006": [...] } — oggetto per anno, non array
      const generalsMap = new Map<number, any>();
      const ppvsByYear = (ppvsByYearRaw as any)?.data ?? ppvsByYearRaw;
      Object.values(ppvsByYear as Record<string, any[]>).forEach((yearSpecifics) => {
        (yearSpecifics ?? []).forEach((specific: any) => {
          const desc = specific.ppv_description;
          if (!desc) return;
          if (!generalsMap.has(desc.id)) generalsMap.set(desc.id, { ...desc, ppvs: [] });
          generalsMap.get(desc.id).ppvs.push(specific);
        });
      });
      setRefData({
        wrestlers: toArr(wrestlersRaw),
        ppvs: Array.from(generalsMap.values()),
        belts: toArr(beltsRaw),
        users: Array.isArray(usersRaw) ? usersRaw : [],
      });
    } finally {
      setRefDataLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    apiGetCards()
      .then((data) => {
        if (isMounted) setCards(Array.isArray(data) ? data : ((data as any).data ?? []));
      })
      .catch(() => {
        if (isMounted) setError('Errore durante il caricamento delle carte.');
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  const expansionOptions = useMemo(() => {
    const map = new Map<number, string>();
    cards.forEach((c) => c.expansions.forEach((e) => map.set(e.id, e.name)));
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [cards]);

  const filtered = useMemo(() => {
    return cards.filter((c) => {
      if (selectedRarity && c.rarity !== selectedRarity) return false;
      if (selectedExpansionId && !c.expansions.some((e) => e.id === selectedExpansionId)) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (
          !c.rarity.includes(q) &&
          !(c.fronte_path ?? '').toLowerCase().includes(q) &&
          !c.wrestlers.some((w) => w.name.toLowerCase().includes(q))
        ) return false;
      }
      return true;
    });
  }, [cards, searchQuery, selectedRarity, selectedExpansionId]);

  return (
    <>
    <CommonWrapper>
      <PageInner>
        <PageHeader>
          <PageTitle>Gestisci Carte</PageTitle>
          <NewBtn onClick={handleOpenModal}>
            + Nuova Carta
          </NewBtn>
        </PageHeader>

        <FiltersRow>
          <FilterSelect
            value={selectedRarity}
            onChange={(e) => setSelectedRarity(e.target.value as CardRarity | '')}
          >
            <option value="">Tutte le rarità</option>
            {RARITIES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={selectedExpansionId}
            onChange={(e) => setSelectedExpansionId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Tutte le espansioni</option>
            {expansionOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </FilterSelect>

          <SearchInput
            placeholder="Cerca wrestler..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {!isLoading && (
            <ResultCount>{filtered.length} carte</ResultCount>
          )}
        </FiltersRow>

        {isLoading && <StatusText>Caricamento...</StatusText>}
        {error && <ErrorText>{error}</ErrorText>}
        {!isLoading && !error && filtered.length === 0 && (
          <StatusText>Nessuna carta trovata.</StatusText>
        )}

        <CardsGrid>
          {filtered.map((card) => {
            const toUrl = (path: string | null | undefined) =>
              path ? (path.startsWith('http') ? path : process.env.REACT_APP_STORAGE_URL + path) : null;
            const imgSrc = toUrl(card.fronte_path);
            const retroSrc = toUrl(card.retro_path);
            return (
              <CardItem key={card.id}>
                <CardImageWrapper>
                  {imgSrc ? (
                    <CardImage src={imgSrc} alt={`Fronte carta ${card.id}`} loading="lazy" />
                  ) : (
                    <CardImagePlaceholder>Nessuna immagine</CardImagePlaceholder>
                  )}
                  {retroSrc && (
                    <CardRetroOverlay>
                      <RetroLabel>RETRO</RetroLabel>
                      <CardImage src={retroSrc} alt={`Retro carta ${card.id}`} loading="lazy" />
                    </CardRetroOverlay>
                  )}
                </CardImageWrapper>
                <CardInfo>
                  <RarityBadge rarity={card.rarity}>{card.rarity}</RarityBadge>
                  <WrestlerNames>
                    {card.wrestlers.length > 0
                      ? card.wrestlers.map((w) => w.name).join(', ')
                      : <em>Nessun wrestler</em>}
                  </WrestlerNames>
                  {card.tags.length > 0 && (
                    <TagsRow>
                      {card.tags.map((t) => <Tag key={t.id}>{t.name}</Tag>)}
                    </TagsRow>
                  )}
                  {(card.author_user || card.author_name) && (
                    <AuthorRow>
                      Autore: <span>
                        {card.author_user
                          ? `${card.author_user.username}${card.author_user.nickname_forum ? ` — "${card.author_user.nickname_forum}"` : ''}`
                          : card.author_name}
                      </span>
                    </AuthorRow>
                  )}
                  {card.price != null
                    ? card.shop_disabled
                      ? <DisabledPriceBadge>{card.price} cr. — disabilitata</DisabledPriceBadge>
                      : <PriceBadge>{card.price} cr.</PriceBadge>
                    : <NoPriceBadge>Non in vendita</NoPriceBadge>
                  }
                  <EditBtn onClick={() => { setEditingCard(card); handleOpenModal(); }}>
                    Modifica
                  </EditBtn>
                  {deleteErrors[card.id] && (
                    <DeleteError>{deleteErrors[card.id]}</DeleteError>
                  )}
                  <DeleteBtn
                    onClick={() => handleDeleteCard(card)}
                    disabled={deletingId === card.id}
                  >
                    {deletingId === card.id ? 'Eliminazione...' : 'Elimina'}
                  </DeleteBtn>
                </CardInfo>
              </CardItem>
            );
          })}
        </CardsGrid>
      </PageInner>
    </CommonWrapper>

    {isModalOpen && (
      <CardCreateModal
        refData={refData}
        refDataLoading={refDataLoading}
        onClose={handleCloseModal}
        card={editingCard ?? undefined}
        onCreated={(newCard) => {
          setCards((prev) => [newCard, ...prev]);
          handleCloseModal();
        }}
        onUpdated={(updatedCard) => {
          setCards((prev) => prev.map((c) => c.id === updatedCard.id ? updatedCard : c));
          handleCloseModal();
        }}
      />
    )}
  </>
  );
};

export default CardListPage;
